import { useCallback, useMemo, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import {
  Alert,
  Box,
  IconButton,
  Table,
  TableContainer,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'

import { isConfiguredParameter, isStartedParameter } from '@components/CommandParameters/helpers'

import type { AutocompleteOption, CommandParametersType } from '@types'

import {
  BODY_VIEW,
  COMMAND_PARAMETER_TYPES_OPTIONS_WITHOUT_FIELD,
  COMMAND_PARAMS_FONT_SIZE,
  FIELD_BIND_VALUE_TYPE_OPTIONS,
  TOOLTIP_SLOT_PROPS,
} from './consts'
import { TableBody } from './TableBody'
import { TableHeader } from './TableHeader'
import { BindType } from './types'

export type CommandConfigRowData = {
  key: string
  name: string
  description?: string | null
  isRequired?: boolean | null
  valueTypeCode?: string | null
  defaultValue?: unknown
  canBeOverridden?: boolean | null
  bindType?: AutocompleteOption<BindType> | null
  value?: string
  fieldPath?: string
}

type CommandConfigTableProps = {
  mode: 'params' | 'body'
  rows: CommandConfigRowData[]
  namePrefix: string
  fieldArrayFields: Array<{ id: string; title?: { id?: string | null } | null }>
  bodyView?: BODY_VIEW
  onBodyViewChange?: (value: BODY_VIEW) => void
  isDisabled?: boolean
  isFieldTypeDisabled?: boolean
  isLoading?: boolean
  defaultRefFieldsObjectOption: AutocompleteOption<string> | null
  refObjectOptions?: AutocompleteOption[]
  canChangeRefObject?: boolean
  onEditJs?: (index: number) => void
}

export const CommandConfigTable = ({
  mode,
  rows = [],
  namePrefix,
  fieldArrayFields,
  bodyView = BODY_VIEW.ALL,
  onBodyViewChange,
  isDisabled = false,
  isFieldTypeDisabled = false,
  isLoading,
  defaultRefFieldsObjectOption,
  refObjectOptions,
  canChangeRefObject,
  onEditJs,
}: CommandConfigTableProps) => {
  const { t } = useTranslation()

  const tableContainerRef = useRef<HTMLDivElement | null>(null)

  const typeOptions = isFieldTypeDisabled
    ? COMMAND_PARAMETER_TYPES_OPTIONS_WITHOUT_FIELD
    : FIELD_BIND_VALUE_TYPE_OPTIONS

  const rowMetaByKey = useMemo(() => {
    const map = new Map<string, CommandConfigRowData>()
    for (const row of rows) map.set(row.key, row)

    return map
  }, [rows])

  const tableRows = useMemo(() => {
    const prepared = fieldArrayFields.map((field, index) => {
      const rowKey = (field as any)?.title?.id as string | undefined
      const rowMeta = rowKey ? rowMetaByKey.get(rowKey) : undefined

      const row: CommandConfigRowData =
        rowMeta ??
        ({
          key: rowKey ?? `__missing:${index}`,
          name: rowKey ?? '—',
          description: null,
          isRequired: null,
          canBeOverridden: null,
          valueTypeCode: null,
          defaultValue: null,
        } as CommandConfigRowData)

      return { row, index, fieldId: field.id }
    })

    if (mode === 'params') {
      return prepared.filter(item => item.row.canBeOverridden == true)
    }

    return prepared
  }, [fieldArrayFields, rowMetaByKey, mode])

  const {
    control,
    formState: { submitCount },
  } = useFormContext()

  const values = useWatch({ control, name: namePrefix as any }) as
    | CommandParametersType[]
    | undefined

  const requiredMissingCount = useMemo(() => {
    if (!tableRows.length) return 0

    return tableRows.reduce((acc, item) => {
      const isRequired = Boolean(item.row.isRequired)
      if (!isRequired) return acc

      const value = values?.[item.index]
      const ok = isConfiguredParameter(value)

      return ok ? acc : acc + 1
    }, 0)
  }, [tableRows, values])

  const firstMissingRequiredRowIndex = useMemo(() => {
    for (const item of tableRows) {
      if (!item.row.isRequired) continue
      const value = values?.[item.index]
      if (!isConfiguredParameter(value)) return item.index
    }

    return null
  }, [tableRows, values])

  const hiddenRowIndexSet = useMemo(() => {
    if (mode !== 'body' || bodyView !== 'configured') return null
    if (!values?.length) return null

    const hidden = new Set<number>()

    for (const item of tableRows) {
      const rowValue = values[item.index]
      const isVisible = isConfiguredParameter(rowValue) || isStartedParameter(rowValue)
      if (!isVisible) hidden.add(item.index)
    }

    return hidden
  }, [tableRows, mode, bodyView, values])

  const handleBodyViewChange = useCallback(
    (_: unknown, value: BODY_VIEW | null) => {
      if (!value) return
      onBodyViewChange?.(value)
    },
    [onBodyViewChange]
  )

  const handleScrollToFirstError = useCallback(() => {
    if (firstMissingRequiredRowIndex == null) return

    const container = tableContainerRef.current
    const rowEl = container?.querySelector(
      `[data-row-index="${firstMissingRequiredRowIndex}"]`
    ) as HTMLElement | null

    rowEl?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [firstMissingRequiredRowIndex])

  return (
    <Box sx={{ width: '100%' }}>
      {mode === 'body' && (
        <Box
          sx={{
            p: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          <ToggleButtonGroup
            exclusive
            size='small'
            value={bodyView}
            sx={{
              '& .MuiToggleButton-root': {
                fontSize: COMMAND_PARAMS_FONT_SIZE,
                px: 1.5,
                py: 0.75,
                textTransform: 'none',
              },
            }}
            onChange={handleBodyViewChange}
          >
            <ToggleButton sx={{ fontSize: COMMAND_PARAMS_FONT_SIZE }} value={BODY_VIEW.ALL}>
              {t('commandParameters.toggle.all')}
            </ToggleButton>
            <ToggleButton sx={{ fontSize: COMMAND_PARAMS_FONT_SIZE }} value={BODY_VIEW.CONFIGURED}>
              {t('commandParameters.toggle.configured')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {submitCount > 0 && requiredMissingCount > 0 && (
        <Alert
          severity='warning'
          sx={{ my: 1 }}
          action={
            <Tooltip
              arrow
              placement='bottom'
              slotProps={TOOLTIP_SLOT_PROPS}
              title={t('commandParameters.buttons.goTo')}
            >
              <IconButton
                aria-label={t('commandParameters.buttons.goTo')}
                color='inherit'
                size='small'
                sx={{ alignSelf: 'center' }}
                onClick={handleScrollToFirstError}
              >
                <ArrowDownwardIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          }
        >
          {t('commandParameters.validation.requiredSummary', { count: requiredMissingCount })}
        </Alert>
      )}

      <TableContainer
        ref={tableContainerRef}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: 320,
        }}
      >
        <Table
          stickyHeader
          size='small'
          sx={{
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              py: '4px',
              px: '8px',
              verticalAlign: 'middle',
              fontSize: '14px',
            },
            '& .MuiInputBase-root': { fontSize: COMMAND_PARAMS_FONT_SIZE },
            '& .MuiInputBase-input': { fontSize: COMMAND_PARAMS_FONT_SIZE },
            '& input::placeholder': { fontSize: COMMAND_PARAMS_FONT_SIZE },
          }}
        >
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '15%' }} />
            <col />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: 40 }} />
          </colgroup>

          <TableHeader />
          <TableBody
            bodyView={bodyView}
            defaultRefFieldsObjectOption={defaultRefFieldsObjectOption}
            hiddenRowIndexSet={hiddenRowIndexSet}
            isChangeRefObjectAllowed={canChangeRefObject}
            isDisabled={isDisabled}
            isLoading={isLoading}
            namePrefix={namePrefix}
            refObjectOptions={refObjectOptions}
            tableRows={tableRows}
            typeOptions={typeOptions}
            onEditJs={onEditJs}
          />
        </Table>
      </TableContainer>
    </Box>
  )
}
